import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Package, ClipboardList, CheckCircle, Wrench, Zap, CreditCard, Headphones, ArrowRight } from 'lucide-react';
import Footer from '../../components/footer';
import { useDarkMode } from '../../components/darkmode/DarkModeContext';

const steps = [
  {
    icon: MapPin,
    number: '01',
    title: 'Pengecekan Area Coverage',
    description: 'Cek ketersediaan layanan di lokasi Anda melalui website atau hubungi customer service kami.',
    details: [
      'Masukkan alamat lengkap di halaman coverage check',
      'Sistem akan menampilkan ketersediaan jaringan fiber optic',
      'Tim survey akan mengkonfirmasi jika diperlukan pengecekan lapangan',
    ],
    color: 'from-blue-500 to-blue-600',
  },
  {
    icon: Package,
    number: '02',
    title: 'Pemilihan Paket Internet',
    description: 'Pilih paket layanan yang sesuai dengan kebutuhan Anda.',
    details: [
      'Bandingkan paket berdasarkan kecepatan, harga, dan fitur',
      'Konsultasi dengan tim sales untuk rekomendasi terbaik',
      'Paket tersedia: Broadband, Dedicated, Metro-E, dan lainnya',
    ],
    color: 'from-green-500 to-green-600',
  },
  {
    icon: ClipboardList,
    number: '03',
    title: 'Pengisian Data Pelanggan',
    description: 'Lengkapi formulir pendaftaran dengan data yang diperlukan.',
    details: [
      'Isi data pribadi: nama, alamat, nomor telepon, email',
      'Upload KTP/identitas resmi untuk verifikasi',
      'Tentukan alamat instalasi dan kontak person di lokasi',
    ],
    color: 'from-purple-500 to-purple-600',
  },
  {
    icon: CheckCircle,
    number: '04',
    title: 'Verifikasi & Konfirmasi Pesanan',
    description: 'Tim kami memverifikasi data dan mengkonfirmasi pesanan Anda.',
    details: [
      'Verifikasi identitas dan kelengkapan dokumen',
      'Konfirmasi paket, harga, dan jadwal instalasi via telepon/WhatsApp',
      'Pembayaran biaya aktivasi (jika ada)',
    ],
    color: 'from-orange-500 to-orange-600',
  },
  {
    icon: Wrench,
    number: '05',
    title: 'Proses Instalasi',
    description: 'Tim teknisi profesional kami melakukan pemasangan di lokasi Anda.',
    details: [
      'Survey lokasi oleh tim teknisi berpengalaman',
      'Penarikan kabel fiber optic dan pemasangan perangkat (ONT/Router)',
      'Konfigurasi jaringan dan pengujian koneksi',
      'Estimasi waktu instalasi: 1-3 hari kerja',
    ],
    color: 'from-red-500 to-red-600',
  },
  {
    icon: Zap,
    number: '06',
    title: 'Aktivasi Layanan',
    description: 'Layanan internet Anda aktif dan siap digunakan!',
    details: [
      'Aktivasi jaringan dan pemberian IP address',
      'Pengujian kecepatan bersama pelanggan',
      'Panduan penggunaan router dan troubleshooting dasar',
      'Penyerahan dokumen kontrak dan kartu pelanggan',
    ],
    color: 'from-teal-500 to-teal-600',
  },
  {
    icon: CreditCard,
    number: '07',
    title: 'Penagihan & Pembayaran',
    description: 'Tagihan bulanan diterbitkan dan dapat dibayar melalui berbagai metode.',
    details: [
      'Tagihan diterbitkan setiap tanggal 1, jatuh tempo tanggal 20',
      'Metode: Transfer bank, virtual account, e-wallet',
      'Notifikasi tagihan melalui email, SMS, dan WhatsApp',
      'Riwayat pembayaran tersedia di dashboard pelanggan',
    ],
    color: 'from-indigo-500 to-indigo-600',
  },
  {
    icon: Headphones,
    number: '08',
    title: 'Support & Maintenance',
    description: 'Dukungan teknis 24/7 dan pemeliharaan jaringan berkala.',
    details: [
      'Customer service tersedia 24/7 via telepon, WhatsApp, dan live chat',
      'Maintenance preventif berkala untuk menjaga kualitas layanan',
      'Response time gangguan kritis < 4 jam',
      'Monitoring jaringan real-time oleh NOC (Network Operations Center)',
    ],
    color: 'from-pink-500 to-pink-600',
  },
];

const AlurTransaksi = () => {
  const { isDarkMode } = useDarkMode();

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
      {/* Hero */}
      <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white pt-28 pb-16">
        <div className="max-w-8xl mx-auto px-4 sm:px-6 text-center">
          <span className="text-green-400 font-semibold text-sm uppercase tracking-wider">Panduan Pelanggan</span>
          <h1 className="text-3xl sm:text-4xl font-bold mt-3 mb-4">Alur Transaksi</h1>
          <p className="text-gray-300 text-lg max-w-2xl mx-auto">
            Proses berlangganan layanan Telnet Indonesia yang mudah, cepat, dan transparan dari awal hingga layanan aktif.
          </p>
        </div>
      </div>

      {/* Timeline */}
      <div className="max-w-8xl mx-auto px-4 sm:px-6 py-16">
        <div className="relative">
          {/* Vertical Line */}

          <div className="space-y-8">
            {steps.map((step, index) => (
              <div key={index} className="relative flex gap-4 sm:gap-8">


                {/* Content Card */}
                <div className={`flex-1 rounded-2xl shadow-sm border p-5 sm:p-6 hover:shadow-md transition-shadow ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-100'}`}>
                  <div className="flex items-center gap-3 mb-2">
                    <span className={`text-xs font-bold uppercase tracking-wider ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>Step {step.number}</span>
                  </div>
                  <h3 className={`text-lg font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{step.title}</h3>
                  <p className={`mb-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>{step.description}</p>
                  <ul className="space-y-2">
                    {step.details.map((detail, dIdx) => (
                      <li key={dIdx} className={`flex items-start gap-2 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        <ArrowRight className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                        <span>{detail}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="mt-16 text-center bg-gradient-to-br from-green-600 to-green-700 rounded-2xl p-8 sm:p-12 text-white">
          <h2 className="text-2xl font-bold mb-3">Siap Berlangganan?</h2>
          <p className="text-green-100 mb-6 max-w-lg mx-auto">
            Mulai dengan mengecek ketersediaan layanan di area Anda. Tim kami siap membantu 24/7.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/coverage-check"
              className="bg-white text-green-700 px-6 py-3 rounded-xl font-semibold hover:bg-green-50 transition-colors"
            >
              Cek Coverage Area
            </Link>
            <a
              href="https://wa.me/628777882022"
              target="_blank"
              rel="noopener noreferrer"
              className="border-2 border-white text-white px-6 py-3 rounded-xl font-semibold hover:bg-white/10 transition-colors"
            >
              Hubungi Kami
            </a>
          </div>
        </div>

        {/* Related Links */}
        <div className="mt-8 text-center">
          <div className="flex justify-center gap-4 text-sm">
            <Link to="/kebijakan-privasi" className="text-green-600 hover:underline">Kebijakan Privasi</Link>
            <Link to="/ketentuan-layanan" className="text-green-600 hover:underline">Ketentuan Layanan</Link>
            <Link to="/faq" className="text-green-600 hover:underline">FAQ</Link>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default AlurTransaksi;
